import { type RefObject, useLayoutEffect, useRef } from 'react';

export const useScrollSync = ({
  refs,
  direction = 'both'
}: {
  refs: RefObject<HTMLElement>[];
  direction?: 'both' | 'horizontal' | 'vertical';
}) => {
  const isScrollingRef = useRef(false);

  function handleScroll(element: HTMLElement) {
    if (isScrollingRef.current) {
      return;
    }

    refs.forEach(ref => {
      const currentRef = ref.current;

      if (currentRef) {
        if (direction === 'vertical' || direction === 'both') {
          currentRef.scrollTop = element.scrollTop;
        }

        if (direction === 'horizontal' || direction === 'both') {
          currentRef.scrollLeft = element.scrollLeft;
        }
      }
    });
  }

  function scrollTo({ left, top }: { left?: number; top?: number }) {
    isScrollingRef.current = true;

    refs.forEach(ref => {
      const currentRef = ref.current;

      if (currentRef) {
        currentRef.scrollTo({ left, top, behavior: 'smooth' });
      }
    });

    setTimeout(() => {
      isScrollingRef.current = false;
    }, 400);
  }

  useLayoutEffect(() => {
    refs.forEach(ref => {
      const currentRef = ref.current;

      currentRef?.addEventListener('scroll', () => handleScroll(currentRef));
    });

    return () => {
      refs.forEach(ref => {
        const currentRef = ref.current;

        if (currentRef) {
          currentRef.removeEventListener('scroll', () => handleScroll(currentRef));
        }
      });
    };
    // since we only want these event listeners to be added once, we can ignore the eslint warning
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { scrollTo };
};
